/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";

export default function EmailVerification() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { isDark } = useDarkMode();

  const token = searchParams.get("token");

  useEffect(() => {
    // If there's a token in the URL, we're verifying an email
    if (token) {
      verifyEmailToken(token);
    }

    // If user is already logged in and verified, redirect to dashboard
    if (user?.emailVerified && !token) {
      navigate("/dashboard");
    }
  }, [token, user, navigate]);

  const verifyEmailToken = async (verificationToken: string) => {
    setIsVerifying(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE}/auth/verify-email?token=${verificationToken}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (response.ok) {
        setMessage(
          data.message || "Email verified successfully! Redirecting...",
        );
        setIsVerified(true);

        // Refresh user data to get updated verification status
        if (user) {
          await refreshUser();
        }

        // Redirect after successful verification
        setTimeout(() => {
          if (user) {
            navigate("/dashboard");
          } else {
            navigate("/login");
          }
        }, 3000);
      } else {
        setError(
          data.error ||
            "Failed to verify email. The link may be invalid or expired.",
        );
      }
    } catch (err: any) {
      setError("Network error. Please check your connection and try again.");
      console.error("Verification error:", err);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    if (!email) {
      setError("Please enter your email address");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE}/auth/resend-verification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        setMessage(
          data.message || "Verification email sent! Please check your inbox.",
        );
      } else {
        setError(
          data.error || "Failed to send verification email. Please try again.",
        );
      }
    } catch (err: any) {
      setError("Network error. Please check your connection and try again.");
      console.error("Resend verification error:", err);
    } finally {
      setLoading(false);
    }
  };

  // If we're verifying a token, show verification status
  if (isVerifying) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center p-4 ${isDark ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <div
          className={`max-w-md w-full p-6 rounded-lg shadow-md ${isDark ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <h2
              className={`text-xl font-semibold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Verifying your email...
            </h2>
            <p className={isDark ? "text-gray-300" : "text-gray-600"}>
              Please wait while we verify your email address.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show success message after verification
  if (isVerified) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center p-4 ${isDark ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <div
          className={`max-w-md w-full p-6 rounded-lg shadow-md ${isDark ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2
              className={`text-xl font-semibold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Email Verified Successfully!
            </h2>
            <p className={isDark ? "text-gray-300" : "text-gray-600"}>
              {message || "Your email has been verified successfully."}
            </p>
            <p
              className={`mt-2 text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}
            >
              Redirecting you shortly...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 ${isDark ? "bg-gray-900" : "bg-gray-50"}`}
    >
      <div
        className={`max-w-md w-full p-6 rounded-lg shadow-md ${isDark ? "bg-gray-800" : "bg-white"}`}
      >
        <div className="text-center mb-6">
          <h1
            className={`text-2xl font-bold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Email Verification
          </h1>
          <p className={isDark ? "text-gray-300" : "text-gray-600"}>
            {token
              ? "Completing your email verification..."
              : "Enter your email to receive a verification link"}
          </p>
        </div>

        {message && (
          <div
            className={`mb-4 p-3 rounded-lg ${
              message.includes("success") || message.includes("sent")
                ? "bg-green-100 text-green-800 border border-green-200"
                : "bg-blue-100 text-blue-800 border border-blue-200"
            }`}
          >
            {message}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-lg border border-red-200">
            {error}
          </div>
        )}

        {!token && (
          <form onSubmit={handleResendVerification} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "border-gray-300 text-gray-900"
                }`}
                placeholder="Enter your email address"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </span>
              ) : (
                "Send Verification Email"
              )}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/login")}
            className={`text-sm ${isDark ? "text-green-400 hover:text-green-300" : "text-green-600 hover:text-green-700"}`}
          >
            Back to Login
          </button>
        </div>

        <div className="mt-6 text-center">
          <p
            className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
          >
            Didn't receive the email? Check your spam folder or request a new
            verification link.
          </p>
        </div>
      </div>
    </div>
  );
}
